import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { cmdbRemove, cmdbShellQuery, cmdbTableQuery, type CiRow } from '../api/cmdb'
import { moduleOf, shellQuery } from '../api/shell'
import { ApiError } from '../api/client'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Forbidden } from '../ui/Forbidden'
import { newAlert, toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CiFormModal } from './CiFormModal'
import { CmdbRowTabs } from './CmdbRowTabs'
import { CmdbViews } from './CmdbViews'
import { esc } from './cells'

/**
 * `Cmdb::index` - lista de elemente de configurare, la /app/cmdb.
 *
 * Aceeasi adresa ca in portal, dinadins: linkurile catre `/cmdb` sunt scrise in
 * bara Overview, in tabloul ITIL, in registrele de domenii si certificate, in
 * ecranele de probleme din Easeedesk si in tabelul de schimbari din Softsee.
 * Un ecran mutat la alta adresa e un ecran pe care nu-l mai gaseste nimeni.
 *
 * COLOANELE
 *
 * Ordinea raspunde, de la stanga la dreapta: ce e, cat conteaza, cum sta acum,
 * daca il mai vedem. `Kind` nu e coloana - clasa il hotaraste oricum, un Server
 * e mereu un Device - dar ramane filtru, unde chiar lucreaza.
 */

const STYLES = ['css/adm.css']
const TABLE_ID = 'cmdbTable'

function nameCell(row: CiRow): string {
  return '<a href="/app/cmdb/view/' + row.ID + '">' + esc(row.name) + '</a>'
}

function serialCell(row: CiRow): string {
  /* Monospace: un serial se citeste caracter cu caracter cand cineva il compara
     cu eticheta de pe spatele masinii. */
  return row.serial ? '<code>' + esc(row.serial) + '</code>' : '<span class="adm-muted">&mdash;</span>'
}

function importanceCell(row: CiRow): string {
  const level = row.criticality
  if (level === null || level < 1 || level > 5) {
    return '<span class="adm-imp adm-imp-0">not set</span>'
  }
  return '<span class="adm-imp adm-imp-' + level + '">' + esc(row.criticality_label) + '</span>'
}

function environmentCell(row: CiRow): string {
  /*
   * Doar productia primeste culoare. Daca toate cinci ar fi colorate, ochiul
   * n-ar mai gasi-o pe cea care conteaza, iar deosebirea care schimba o
   * hotarare e productie sau nu.
   */
  if (!row.environment) return '<span class="adm-muted">not known</span>'
  return row.environment === 1
    ? '<span class="adm-pill adm-pill-warn">' + esc(row.environment_label) + '</span>'
    : '<span class="adm-muted">' + esc(row.environment_label) + '</span>'
}

function actionsCell(row: CiRow, canEdit: boolean, canDelete: boolean): string {
  /* `<a>`, nu `<button>`: asa merge si click-mijloc si "deschide in tab nou",
     pe care le foloseste oricine compara doua elemente. */
  const open =
    '<a class="btn btn-xs adm-row-action adm-row-inventoree" href="/app/cmdb/view/' +
    row.ID +
    '" target="_blank" rel="noopener" title="Everything about this item: properties, connections, impact and tickets">' +
    '<i class="fas fa-list-alt"></i> Advanced</a>'
  if (!canEdit) return open
  const edit =
    '<button class="btn btn-xs btn-primary adm-row-action cmdbEditRow" title="Edit" data-id="' +
    row.ID +
    '"><i class="fa fa-pencil-alt"></i></button>'
  /*
   * Un element intretinut de descoperire SE POATE edita, dar nu se poate
   * retrage. Cele doua fusesera ascunse impreuna in portal si nu tin impreuna:
   * retragerea e refuzata si de server - elementul se intoarce la urmatorul
   * check-in - dar editarea e singurul fel in care i se poate spune cat
   * conteaza, fiindca importanta o completeaza numai omul.
   */
  if (!row.manual) {
    return (
      open +
      edit +
      '<span class="adm-muted adm-row-action" title="Stands for a discovered device: it cannot be retired here, and its customer follows the equipment. Everything else is yours to set."><i class="fas fa-robot"></i></span>'
    )
  }
  if (!canDelete) return open + edit
  return (
    open +
    edit +
    '<button class="btn btn-xs btn-danger adm-row-action cmdbRemoveRow" title="Retire" data-id="' +
    row.ID +
    '"><i class="fa fa-times"></i></button>'
  )
}

export function cmdbColumns(canEdit: boolean, canDelete: boolean): TableColumn[] {
  const as = (row: Record<string, unknown>) => row as unknown as CiRow
  return [
    {
      /*
       * Sageata de deschidere, prima coloana - ca in portal.
       *
       * Fara ea randul nu se putea desface deloc, si asta e chiar ce a spus
       * omul uitandu-se la ecran: „lipseste expandul, nu e ca pe cec".
       * `ExpandRow` din `general.js` se lega de `center.show_details`, si
       * `DataTable` din v4 face acelasi lucru cu aceeasi clasa.
       */
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (r) =>
        '<center class="show_details color-dark-grey pointer font-16" data-id="' +
        as(r).ID +
        '"><i class="fas fa-plus-circle"></i></center>',
    },
    { index: 1, title: 'Name', orderable: true, visible: true, html: (r) => nameCell(as(r)) },
    { index: 2, title: 'Class', orderable: true, visible: true, html: (r) => esc(as(r).class_name) },
    {
      index: 3,
      title: 'Serial',
      orderable: true,
      visible: true,
      className: 'adm-nowrap',
      html: (r) => serialCell(as(r)),
    },
    { index: 4, title: 'Importance', orderable: true, visible: true, html: (r) => importanceCell(as(r)) },
    { index: 5, title: 'Environment', orderable: false, visible: true, html: (r) => environmentCell(as(r)) },
    {
      index: 6,
      title: 'Status',
      orderable: true,
      visible: true,
      html: (r) => '<span class="adm-pill ' + as(r).status_pill + '">' + esc(as(r).status_label) + '</span>',
    },
    {
      index: 7,
      title: 'Health',
      orderable: true,
      visible: true,
      html: (r) => '<span class="adm-pill ' + as(r).health_pill + '">' + esc(as(r).health_label) + '</span>',
      /*
       * Randul intreg poarta starea, nu doar pastila din coloana Health: lista
       * se citeste dintr-o privire, iar o pastila mica intr-a sasea coloana
       * cere sa fie cautata rand cu rand.
       */
      rowClass: (r) => {
        const state = as(r).health
        if (state === 3) return 'adm-row-down'
        if (state === 2) return 'adm-row-degraded'
        return undefined
      },
    },
    {
      index: 8,
      title: 'Last seen',
      orderable: true,
      visible: true,
      className: 'adm-nowrap adm-muted',
      html: (r) => esc(as(r).last_seen ?? ''),
    },
    {
      index: 9,
      title: 'Relations',
      orderable: true,
      visible: true,
      className: 'adm-numeric',
      html: (r) => String(as(r).relations),
    },
    {
      index: 10,
      title: '',
      orderable: false,
      visible: true,
      width: 190,
      className: 'text-right adm-nowrap',
      html: (r) => actionsCell(as(r), canEdit, canDelete),
    },
  ]
}

/**
 * Coloana din tabel => coloana din lista alba a serverului.
 *
 * Serverul primeste indicii din `Ci::$sortable`, unde 0 e sageata de deschidere
 * si 10 butoanele - deci ZERO, de cand sageata e la locul ei si coloanele
 * numara la fel de amandoua partile.
 *
 * A fost 1 cat timp lipsea sageata, si atunci hartile chiar se despartisera:
 * un click pe „Name" trimitea 0, iar 0 nu e in lista alba a serverului, deci
 * lista se intorcea in ordinea implicita si antetul arata o sageata care nu
 * insemna nimic. Nu se vedea ca eroare - se vedea ca o sortare care „nu prinde".
 */
const SORT_OFFSET = 0

export function CmdbListPage() {
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(cmdbShellQuery)
  const query = useQueryClient()

  const [classId, setClassId] = useState(0)
  const [kind, setKind] = useState(0)
  const [status, setStatus] = useState('')
  const [environment, setEnvironment] = useState('')
  const [unhealthy, setUnhealthy] = useState(false)
  const [editing, setEditing] = useState<number | null>(null)

  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    /* Cele critice primele, ca in portal: o lista de sute sortata alfabetic
       pune in cap ce incepe cu A, si niciodata lucrul pentru care s-a deschis
       ecranul. */
    order: { column: 3, dir: 'desc' },
    search: '',
  })

  const canEdit = page.data?.canEdit ?? false
  const canDelete = page.data?.canDelete ?? false
  const columns = useMemo(() => cmdbColumns(canEdit, canDelete), [canEdit, canDelete])

  const table = useQuery({
    ...cmdbTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      search: state.search,
      ci_class_id: classId,
      kind,
      status,
      environment,
      unhealthy,
      order: { column: state.order.column + SORT_OFFSET, dir: state.order.dir },
    }),
    placeholderData: keepPreviousData,
  })

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const reload = () => {
    void query.invalidateQueries({ queryKey: ['cmdb', 'table'] })
  }

  const retire = (id: number) => {
    if (!window.confirm('Retire this item? Its relations go quiet with it.')) return
    void cmdbRemove(id)
      .then((result) => {
        if (result.status !== 'OK') {
          newAlert('<i class="fas fa-exclamation-circle"></i> ' + result.message, 'danger', 7000, document.querySelector('#admWarning'))
          return
        }
        toast(result.message, 'CMDB')
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'CMDB', 'error'))
  }

  const onRowClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const button = (event.target as HTMLElement).closest('button')
    if (!button) return
    const id = Number(button.getAttribute('data-id') ?? 0)
    if (id < 1) return
    if (button.classList.contains('cmdbEditRow')) setEditing(id)
    if (button.classList.contains('cmdbRemoveRow')) retire(id)
  }

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <Content>
      {/* Banda celor patru vederi, prima in `<div id="content">` ca in portal. */}
      <CmdbViews views={page.data.views} current="cmdb" />
      <div onClick={onRowClick}>
        <DataTable
          id={TABLE_ID}
          columnTools
          columns={columns}
          rows={rows}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.error ? table.error.message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => String(row.DT_RowId ?? `ci_${row.ID}`)}
          /* Randul desfacut: aceleasi opt file ca in portal, cu Quick Access
             langa ele. Vezi antetul din `CmdbRowTabs.tsx`. */
          details={(row) => <CmdbRowTabs id={Number(row.ID)} />}
          headerButtons={
            canEdit ? (
              <>
                <button className="btn btn-success tk-header-extra" onClick={() => setEditing(0)}>
                  <i className="fas fa-plus"></i> Add
                </button>
                {/* Adresa e cea din portal (`/cmdb/import`) si duce in /app prin
                    randul din `MOVED`. Cere acelasi drept ca „Add": importul e o
                    scriere. Un buton nedesenat n-a oprit niciodata pe nimeni -
                    gardul adevarat e pe server. */}
                <a href="/app/cmdb/import" className="btn btn-default tk-header-extra" id="cmdbImportLink">
                  <i className="fas fa-file-import"></i> Import
                </a>
              </>
            ) : undefined
          }
          filters={{
            active: [classId, kind, status !== '' ? 1 : 0, environment !== '' ? 1 : 0, unhealthy ? 1 : 0].filter(Boolean)
              .length,
            render: (close) => (
              <div className="adm-bar" id="cmdbFilters">
                <div className="adm-field">
                  <label htmlFor="cmdbKind">Type</label>
                  <select
                    className="form-control input-sm"
                    id="cmdbKind"
                    value={kind}
                    onChange={(e) => {
                      setKind(Number(e.target.value))
                      setState({ start: 0 })
                    }}
                  >
                    <option value={0}>Any type</option>
                    {page.data.kinds.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="adm-field">
                  <label htmlFor="cmdbEnvironment">Environment</label>
                  {/*
                   * "Not known" e o optiune, nu o lipsa: toate elementele de
                   * dinainte de migrare intra cu 0, si lista aia e chiar ce
                   * trebuie completat inainte ca filtrul sa insemne ceva.
                   */}
                  <select
                    className="form-control input-sm"
                    id="cmdbEnvironment"
                    value={environment}
                    onChange={(e) => {
                      setEnvironment(e.target.value)
                      setState({ start: 0 })
                    }}
                  >
                    <option value="">Any environment</option>
                    <option value="0">Not known</option>
                    {page.data.environments.map((env) => (
                      <option key={env.value} value={String(env.value)}>
                        {env.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="adm-field adm-field-wide">
                  <label htmlFor="cmdbClass">Class</label>
                  <select
                    className="form-control input-sm"
                    id="cmdbClass"
                    value={classId}
                    onChange={(e) => {
                      setClassId(Number(e.target.value))
                      setState({ start: 0 })
                    }}
                  >
                    <option value={0}>Any class</option>
                    {page.data.classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="adm-field">
                  <label htmlFor="cmdbStatus">Status</label>
                  <select
                    className="form-control input-sm"
                    id="cmdbStatus"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value)
                      setState({ start: 0 })
                    }}
                  >
                    <option value="">Any status</option>
                    {page.data.statuses.map((s) => (
                      <option key={s.value} value={String(s.value)}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/*
                 * Bifa, nu al patrulea selector. Intrebarea e da/nu - "arata-mi
                 * numai ce e rupt acum" - iar un selector cu trei optiuni ar
                 * sugera ca exista si "numai operationale", care e lista
                 * intreaga fara cateva randuri.
                 */}
                <span className="adm-check">
                  <SettingsCheck
                    id="cmdbUnhealthy"
                    title="Only items that are down, or degraded because something under them is"
                    checked={unhealthy}
                    onChange={(on) => {
                      setUnhealthy(on)
                      setState({ start: 0 })
                    }}
                    label="Only down or degraded"
                  />
                </span>
                <span className="adm-spacer"></span>
                <button className="btn btn-default btn-sm closeFilters" onClick={close} style={{ marginBottom: 2 }}>
                  Close
                </button>
              </div>
            ),
          }}
          footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
        />
      </div>
      <div id="admWarning" className="adm-warning"></div>
      {editing !== null && (
        <CiFormModal
          id={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </Content>
  )
}

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}
